// --- React & Redux
import React from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import { Row } from 'antd';
import Button from '@dtci/spellbook/Button';
import Input from '@dtci/spellbook/Input';
import moment from 'moment';
// --- API & Utils
import { datastageApi } from 'datastage/api';
import { getAssociationIdsAndErrors, isCreateAssociationDisabled } from './utils/validations';
// --- Hooks & Store
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useRightsActions } from 'datastage/store/rights/useRights';
import { useToasts } from 'datastage/utils/hooks/useToasts';
// --- Components
import { InputLabel } from 'datastage/components/InputLabel/InputLabel';
import { InputSearch } from './input-search';
import { StaticSearch } from './static-search';
// --- Styles
import '../styles.scss';

/**
 * Associations Form
 *
 * Features:
 * - Displays right details
 * - Handles creating or fixing associations
 *
 * @param details
 * @returns {Element}
 * @constructor
 */
export function AssociationsForm({ details }) {
    const { updateRights, clearAssociationsDrawer } = useRightsActions();
    const { showErrorToast, showSuccessToast } = useToasts();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { associationForm } = useSelector((state) => state.rights);
    const { showSpinner: isLoading } = useSelector((state) => state.spinner);

    // Fields that need UTC date formatting (term dates)
    const utcDateFields = ['actualTermStart', 'actualTermEnd'];
    // Fields that need local date formatting
    const localDateFields = ['rightCreated'];

    /**
     * Format date value based on field type.
     * Term dates use UTC, created date uses local timezone.
     */
    const formatDateValue = (key, value) => {
        if (!value) return '';
        if (utcDateFields.includes(key)) {
            // UTC for term dates (like old version)
            return moment(moment(value).utc().format('YYYY-MM-DD[T]HH:mm:ss')).format('MM/DD/YYYY');
        }
        if (localDateFields.includes(key)) {
            // Local for created date (like old version)
            return moment(moment(value).local().format('YYYY-MM-DD[T]HH:mm:ss')).format('MM/DD/YYYY');
        }
        return value;
    };

    // Close the drawer and clear form state
    const handleClose = () => {
        updateRights({ isAssociateOpen: false });
        clearAssociationsDrawer();
    };

    // Fix a failed association
    const handleFixFailedAssociation = async () => {
        setLoadingTrue();
        try {
            const { errors, dealId, catalogId, previousDeal, previousCatalog } = getAssociationIdsAndErrors({
                dealNotFound: associationForm?.dealNotFound,
                catalogNotFound: associationForm?.catalogNotFound,
                dealSelected: associationForm?.dealSelected,
                catalogSelected: associationForm?.catalogSelected,
                details,
            });

            if (Object.keys(errors).length === 0) {
                const data = await datastageApi.fixFailedAssociation({
                    id: null,
                    rightActiveId: null,
                    rightId: details?.failedRelationship?.childRightsLineId,
                    dealId,
                    catalogId,
                    previousDeal,
                    previousCatalog,
                });

                if (data?.code) {
                    showErrorToast(data?.message || 'Failed to fix association.');
                } else {
                    showSuccessToast(data?.message || 'Association fixed successfully.');
                    handleClose();
                    // Trigger grid refresh
                    updateRights({ refreshRights: true });
                }
            }
        } catch (error) {
            showErrorToast(error?.message || 'Failed to fix association.');
            console.error('Failed to fix association:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Create a new association for the selected rightset
    const handleCreateAssociation = async () => {
        setLoadingTrue();
        try {
            let errors = {};
            if (!associationForm?.dealSelected?.id && !associationForm?.catalogSelected?.id) {
                errors.dealSelected = 'Deal or Catalog is mandatory and cannot be blank';
                errors.catalogSelected = 'Deal or Catalog is mandatory and cannot be blank';
            }

            if (Object.keys(errors).length === 0) {
                const res = await datastageApi.createAssociation({
                    id: details?.associationEntity?.id ?? null,
                    rightActiveId: details?.id,
                    rightId: details?.rightId,
                    dealId: associationForm?.dealSelected?.id ?? null,
                    catalogId: associationForm?.catalogSelected?.id ?? null,
                });

                if (res?.code) {
                    showErrorToast(res?.message || 'Failed to create association.');
                } else {
                    showSuccessToast(res?.message || 'Association created successfully.');
                    handleClose();
                    // Trigger grid refresh
                    updateRights({ refreshRights: true });
                }
            }
        } catch (error) {
            showErrorToast(error?.message || 'Failed to create association.');
            console.error('Failed to create association:', error);
        } finally {
            setLoadingFalse();
        }
    };

    return (
        <>
            <Row>
                {[
                    { key: 'rightId', label: 'Right ID' },
                    { key: 'rightTitle', label: 'Title' },
                    { key: 'rightTemplate', label: 'Template' },
                    { key: 'rightMedia', label: 'Media' },
                    { key: 'rightTerritory', label: 'Territory' },
                    { key: 'rightLanguage', label: 'Language' },
                    { key: 'actualTermStart', label: 'Actual Term Start' },
                    { key: 'actualTermEnd', label: 'Actual Term End' },
                    { key: 'startDateStatus', label: 'Start Date Status' },
                    { key: 'endDateStatus', label: 'End Date Status' },
                    { key: 'rightCreated', label: 'Right Created' },
                ].map(({ key, label }) => (
                    <InputLabel key={key} colProps={{ span: 24 }} label={label}>
                        <Input
                            value={
                                dateFields.includes(key)
                                    ? formatDateValue(key, details[key])
                                    : details[key] ?? ''
                            }
                            width="auto"
                            id={key}
                            name={key}
                            isDisabled
                        />
                    </InputLabel>
                ))}
            </Row>
            {details?.failedRelationship !== null && (
                <Row>
                    <InputLabel colProps={{ span: 24 }} label="Error Message">
                        <Input
                            value={details?.failedRelationship?.loadErrorMsg}
                            width="auto"
                            isDisabled
                        />
                    </InputLabel>
                </Row>
            )}
            <StaticSearch details={details} />
            <InputSearch details={details} />
            <Row>
                <div className="button-row">
                    <Button
                        iconType="fa-solid fa-xmark"
                        labelText="close"
                        styleType="link"
                        onClick={handleClose}
                    >
                        Close
                    </Button>

                    {details?.failedRelationship == null ? (
                        <Button
                            iconType="fa-solid fa-wrench"
                            labelText="create"
                            styleType="link"
                            disabled={isCreateAssociationDisabled(details) || isLoading}
                            onClick={handleCreateAssociation}
                        >
                            Create Association
                        </Button>
                    ) : (
                        <Button
                            iconType="fa-solid fa-wrench"
                            labelText="fix"
                            styleType="link"
                            disabled={isLoading}
                            onClick={handleFixFailedAssociation}
                        >
                            Fix Failed Association
                        </Button>
                    )}
                </div>
            </Row>
        </>
    );
}
